import { useEffect, useRef, useState } from 'react';

type City = { id: string; name: string; count: number };

export function DealerCityPicker({ cities, value, disabled, onChange }: {
  cities: City[];
  value: string;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const selected = cities.find((city) => city.id === value);

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const selectedButton = dialogRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]');
    (selectedButton ?? dialogRef.current?.querySelector<HTMLButtonElement>('.dealer-city-picker__option'))?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
      const buttons = Array.from(dialogRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
      if (!buttons.length) return;
      const first = buttons[0]!;
      const last = buttons[buttons.length - 1]!;
      if (event.key === 'Tab') {
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
      const options = buttons.filter((button) => button.classList.contains('dealer-city-picker__option'));
      const index = options.indexOf(document.activeElement as HTMLButtonElement);
      if (index < 0) return;
      let next = index;
      if (event.key === 'ArrowDown') next = Math.min(index + 1, options.length - 1);
      else if (event.key === 'ArrowUp') next = Math.max(index - 1, 0);
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = options.length - 1;
      else return;
      event.preventDefault();
      options[next]?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
      trigger?.focus();
    };
  }, [open]);

  return <>
    <span className="station-search__label" id="dealer-city-label">Город или регион</span>
    <button ref={triggerRef} type="button" className="dealer-city-picker__trigger" disabled={disabled}
      aria-labelledby="dealer-city-label dealer-city-value" aria-haspopup="dialog" aria-expanded={open}
      onClick={() => setOpen(true)}>
      <svg className="dealer-city-picker__pin" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z" /><circle cx="12" cy="10" r="2.5" />
      </svg>
      <span id="dealer-city-value">{selected?.name ?? 'Выберите город или регион'}</span>
      <svg className="dealer-city-picker__chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
    </button>
    {open ? <div className="modal-overlay dealer-city-picker__overlay" onClick={() => setOpen(false)}>
      <div ref={dialogRef} className="modal dealer-city-picker" role="dialog" aria-modal="true"
        aria-labelledby="dealer-city-title" onClick={(event) => event.stopPropagation()}>
        <div className="dealer-city-picker__heading">
          <div><h2 className="modal__title" id="dealer-city-title">Выберите город или регион</h2>
            <p>Количество дилеров — рядом с названием</p></div>
          <button className="dealer-city-picker__close" type="button" aria-label="Закрыть список" onClick={() => setOpen(false)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
          </button>
        </div>
        <ul className="dealer-city-picker__list">
          {cities.map((city) => <li key={city.id}>
            <button className="dealer-city-picker__option" type="button" aria-pressed={city.id === value}
              onClick={() => { onChange(city.id); setOpen(false); }}>
              <span>{city.name}</span>
              <span className="dealer-city-picker__count" aria-label={`Дилеров: ${city.count}`}>{city.count}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
            </button>
          </li>)}
        </ul>
      </div>
    </div> : null}
  </>;
}
